import React, { useState, ComponentType } from 'react';
import { ApolloClient, useMutation, useQuery } from '@apollo/client';
import _get from 'lodash/get';
import { RouteProps, RedirectProps } from 'react-router-dom';

import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar } from '@trqk/utils';
import { LoadingEllipses } from '..';

const { GET_SESSION_IS_VALID, VALIDATE_COGNITO_USER_SESSION } = queryStrings;

/**
 * @typedef {PrivateRouteProps}
 * @property {AppKey} appKey the app key, i.e. 'web' for packages/web or 'admin' for packages/admin
 * @property {ApolloClient<any>} client an instance of ApolloClient returned by useApolloClient() in
 * the consuming application
 * @property {JSX.Element} component the page to render in this route
 * @property {string | string[]} path the `path` prop passed to the Route from 'react-router-dom'
 * @property {string} redirectPath where to send the user if they are not logged in
 * @property {boolean} exact the `exact` prop passed to the Route from from 'react-router-dom'
 * @property {boolean} strict the `strict` prop passed to the Route from from 'react-router-dom'
 * @property {any | undefined} location the `location` prop passed to the Route from from 'react-router-dom'
 * @property {boolean} sensitive the `sensitive` prop passed to the Route from from 'react-router-dom'
 * @property {boolean} ready if false, this component will return a loading indicator
 * @property {React.Component<RedirectProps>} Redirect the Redirect component from 'react-router-dom'.
 * This needs to be passed in from the consuming application so that it has access to the BrowserRouter
 * context; otherwise we'll see an error like: `You should not use <Redirect> outside a <Router>`.
 * @property {boolean} render if ready is true and render is false, this component will redirect
 * the user to the redirectPath
 * @property {React.Component<RouteProps>} Route the Route component from 'react-router-dom'. This
 * needs to be passed in from the consuming application so that it has access to the BrowserRouter
 * context; otherwise we'll see an error like: `You should not use <Route> outside a <Router>`.
 */
interface PrivateRouteProps {
    appKey: AppKey;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    client: ApolloClient<any>;
    component(): JSX.Element;
    path: string | string[];
    redirectPath: string;
    exact?: boolean;
    strict?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    location?: any | undefined;
    sensitive?: boolean;
    ready?: boolean;
    Redirect: ComponentType<RedirectProps>;
    render?: boolean;
    Route: ComponentType<RouteProps>;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * A private route which redirects a user to a given path if they are not currently logged in.
 * @param {PrivateRouteProps} props
 */
export const PrivateRoute = ({
    appKey,
    client,
    component: Component,
    ready = true,
    redirectPath,
    Redirect,
    render = true,
    Route,
    ...rest
}: PrivateRouteProps): JSX.Element => {
    const clientId = getWebpackEnvVar(appKey, 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar(appKey, 'AWS_USER_POOL_ID');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkedAuthentication, setCheckedAuthentication] = useState(false);
    const [validateCognitoUserSession] = useMutation(
        VALIDATE_COGNITO_USER_SESSION,
        {
            client,
            onCompleted: data => {
                const sessionIsValid = _get(data, [
                    'validateCognitoUserSession',
                    'sessionIsValid',
                ]);

                if (sessionIsValid) {
                    setIsAuthenticated(true);
                }

                setCheckedAuthentication(true);
            },
        },
    );

    useQuery(GET_SESSION_IS_VALID, {
        client,
        onCompleted: data => {
            const sessionIsValid = _get(data, ['session', 'sessionIsValid']);

            if (sessionIsValid) {
                setIsAuthenticated(true);
                setCheckedAuthentication(true);
            } else {
                validateCognitoUserSession({
                    variables: {
                        clientId,
                        userPoolId,
                    },
                });
            }
        },
    });

    if (!ready) {
        return (
            <div className="flex items-center justify-center w-screen h-screen">
                <LoadingEllipses fillColor="primary-300" width={80} />
            </div>
        );
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    return (
        <Route
            {...rest}
            render={(props: any): JSX.Element => {
                if (!checkedAuthentication) {
                    return <div />;
                }

                if (render && checkedAuthentication && isAuthenticated) {
                    return <Component {...props} />;
                }

                const redirectTo = {
                    pathname: redirectPath,
                    state: {
                        from: props.location,
                    },
                };

                return <Redirect to={redirectTo} />;
            }}
        />
    );
};
